/**
 * Pet API Request Deduplication & Caching Utility
 * 
 * Prevents multiple concurrent /pets requests on page load by:
 * 1. Single in-flight request enforcement
 * 2. Session-level caching
 * 3. Returning the same Promise if request is already pending
 */

// In-flight request tracking
let pendingRequest = null;

// Session-level cache
let cachedPets = null;
let cacheTimestamp = null;
const CACHE_TTL = 30000; // 30 seconds

// Debug mode for tracing
const DEBUG = true;

function log(...args) {
  if (DEBUG) {
    console.log('[PetApiCache]', ...args);
  }
}

function getCallerInfo() {
  try {
    const stack = new Error().stack;
    const lines = stack.split('\n');
    // Find the first line that's not from this file
    for (let i = 2; i < lines.length; i++) {
      const line = lines[i];
      if (!line.includes('petApiCache.js')) {
        return line.trim();
      }
    }
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Fetch user's pets with deduplication
 * @param {string} userId - The user ID to fetch pets for
 * @param {string} backendUrl - The backend URL
 * @param {Object} options - Options
 * @param {boolean} options.forceRefresh - Bypass cache and force a new request
 * @returns {Promise<{pets: Array}>} - The pets data
 */
export async function fetchUserPets(userId, backendUrl, options = {}) {
  const caller = getCallerInfo();
  log(`fetchUserPets called from: ${caller}`);
  log(`userId: ${userId}, forceRefresh: ${options.forceRefresh}`);

  // Validate input
  if (!userId) {
    log('❌ No userId provided, returning empty');
    return { pets: [] };
  }

  // Check cache first (unless force refresh)
  if (!options.forceRefresh && cachedPets && cacheTimestamp) {
    const age = Date.now() - cacheTimestamp;
    if (age < CACHE_TTL) {
      log(`✅ Returning cached pets (age: ${age}ms)`);
      return { pets: cachedPets };
    } else {
      log(`⏰ Cache expired (age: ${age}ms)`);
    }
  }

  // If there's already a pending request, return it
  if (pendingRequest) {
    log('🔄 Request already in flight, returning pending promise');
    return pendingRequest;
  }

  // Create new request with timeout
  log('🚀 Starting new fetch request');
  const FETCH_TIMEOUT = 15000; // 15 second timeout
  
  pendingRequest = (async () => {
    try {
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT);
      
      // Request only 1 pet (most recent) with images for performance
      // The backend now defaults to limit=1, but be explicit
      const url = `${backendUrl}/api/users/${userId}/pets?limit=1`;
      log(`📡 Fetching: ${url}`);
      
      const response = await fetch(url, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        log(`✅ Fetch successful, got ${data.pets?.length || 0} pets`);
        
        // Update cache
        cachedPets = data.pets || [];
        cacheTimestamp = Date.now();
        
        return { pets: cachedPets };
      } else {
        log(`❌ Fetch failed with status: ${response.status}`);
        return { pets: [] };
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        log(`⏱️ Fetch timed out after ${FETCH_TIMEOUT}ms`);
      } else {
        log(`❌ Fetch error: ${error.message}`);
      }
      throw error;
    } finally {
      // Clear pending request
      pendingRequest = null;
    }
  })();

  return pendingRequest;
}

/**
 * Clear the cache (call on logout or explicit invalidation)
 */
export function clearPetCache() {
  log('🧹 Clearing pet cache');
  cachedPets = null;
  cacheTimestamp = null;
  pendingRequest = null;
}

/**
 * Update cache with new pet data (call after creating a new pet)
 * @param {Object} pet - The new pet to add to cache
 */
export function addPetToCache(pet) {
  if (cachedPets) {
    log('➕ Adding pet to cache:', pet.name);
    cachedPets = [pet, ...cachedPets];
  }
}

/**
 * Check if a request is currently in flight
 * @returns {boolean}
 */
export function isRequestPending() {
  return pendingRequest !== null;
}

export default {
  fetchUserPets,
  clearPetCache,
  addPetToCache,
  isRequestPending
};
